<script setup>
import { computed, ref, reactive } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();

const isCreating = ref(false);
const accountId = computed(() => route.params.accountId);

// Agent type is always LLM (other types are managed in IA platform)
const AGENT_TYPE = 'llm';

// Provider e Model são FIXOS e nativos da IA
// A API Key da OpenAI é configurada na conta do cliente na IA
const FIXED_PROVIDER = 'openai';
const FIXED_MODEL = 'gpt-4o'; // GPT-4o Multimodal (padrão IA)

const state = reactive({
  name: '',
  description: '',
  role: '',
  goal: '',
  instruction: '',
  temperature: 0.7,
});

const isValidName = computed(() => /^[a-zA-Z0-9_-]+$/.test(state.name));
const canSubmit = computed(() => state.name && isValidName.value);

const handleSubmit = async () => {
  if (!canSubmit.value) return;

  try {
    isCreating.value = true;
    const payload = {
      name: state.name,
      description: state.description,
      agent_type: AGENT_TYPE,
      role: state.role,
      goal: state.goal,
      instruction: state.instruction,
      model: FIXED_MODEL, // gpt-4o (fixo)
      provider: FIXED_PROVIDER, // openai (fixo)
      temperature: parseFloat(state.temperature),
    };

    const result = await store.dispatch('aifocusAgents/create', payload);
    useAlert('Agente criado e sincronizado com IA');

    router.push({
      name: 'aifocus_agents_settings_index',
      params: { accountId: accountId.value, agentId: result.id },
    });
  } catch (error) {
    useAlert(error?.message || 'Erro ao criar agente');
  } finally {
    isCreating.value = false;
  }
};

const handleCancel = () => {
  router.push({
    name: 'aifocus_agents_settings_index',
    params: { accountId: accountId.value },
  });
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <PageLayout :show-pagination-footer="false" :show-know-more="false">
    <template #header>
      <div class="flex items-center justify-between w-full px-6 py-4">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            Criar Novo Agente
          </h1>
          <p class="text-sm text-n-slate-11">
            Configure um novo agente de IA para IA
          </p>
        </div>
      </div>
    </template>

    <template #body>
      <div class="px-6 pb-6">
        <div class="max-w-3xl mx-auto">
          <div class="bg-n-solid-1 border border-n-weak rounded-xl p-6">
            <div class="grid gap-6">
              <!-- Basic Info -->
              <div class="grid gap-4">
                <h3 class="text-base font-medium text-n-slate-12">
                  Informacoes Basicas
                </h3>

                <Input
                  v-model="state.name"
                  label="Nome do Agente"
                  placeholder="meu_agente (sem espacos)"
                  :message="
                    state.name && !isValidName
                      ? 'Use apenas letras, numeros, _ e -'
                      : ''
                  "
                  :message-type="state.name && !isValidName ? 'error' : 'info'"
                />

                <Input
                  v-model="state.description"
                  label="Descricao"
                  placeholder="Descreva o proposito do agente"
                />
              </div>

              <!-- Role & Goal -->
              <div class="grid gap-4">
                <h3 class="text-base font-medium text-n-slate-12">
                  Comportamento do Agente
                </h3>

                <Input
                  v-model="state.role"
                  label="Role (Funcao)"
                  placeholder="Ex: Consultor Virtual de Vendas"
                />

                <Input
                  v-model="state.goal"
                  label="Goal (Objetivo)"
                  placeholder="Ex: Apresentar solucao, qualificar leads"
                />
              </div>

              <!-- LLM Config -->
              <div class="grid gap-4">
                <h3 class="text-base font-medium text-n-slate-12">
                  Configuracao do Modelo
                </h3>

                <!-- Info sobre modelo fixo -->
                <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p class="text-sm text-blue-900">
                    <strong>Modelo:</strong> GPT-4o (Multimodal) - OpenAI
                  </p>
                  <p class="text-xs text-blue-700 mt-1">
                    Modelo padrão da IA. A API Key da OpenAI é configurada
                    na sua conta da IA.
                  </p>
                </div>

                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-n-slate-12">
                    Instruction (System Prompt)
                  </label>
                  <textarea
                    v-model="state.instruction"
                    rows="6"
                    class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 resize-none"
                    placeholder="Instrucoes do sistema para o agente..."
                  />
                </div>

                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-n-slate-12">
                    Temperature: {{ state.temperature }}
                  </label>
                  <input
                    v-model="state.temperature"
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    class="w-full"
                  />
                  <p class="text-xs text-n-slate-10">
                    Quanto maior, mais criativo. Quanto menor, mais preciso.
                  </p>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center gap-3 pt-4 border-t border-n-weak">
                <Button
                  label="Criar e Sincronizar"
                  :is-loading="isCreating"
                  :disabled="!canSubmit"
                  @click="handleSubmit"
                />
                <Button
                  label="Cancelar"
                  variant="secondary"
                  @click="handleCancel"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
